import { TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

/** Değişim yüzdesi; `goodWhenUp=false` iken artış kırmızı gösterilir (gider gibi). */
export function TrendChip({ trend, goodWhenUp = true, label }: { trend: { changePct: number | null }; goodWhenUp?: boolean; label?: string }) {
  const { t } = useTranslation()
  if (trend.changePct === null) return <span className="text-xs text-muted-foreground">{t('common.noComparison')}</span>
  const up = trend.changePct >= 0
  const good = up === goodWhenUp
  const Icon = up ? TrendingUp : TrendingDown
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      <span className={cn('inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-semibold', good ? 'bg-success/12 text-success' : 'bg-destructive/12 text-destructive')}>
        <Icon className="size-3" aria-hidden />%{Math.abs(trend.changePct).toLocaleString('tr-TR')}
      </span>
      {label ?? t('common.vsPrevMonth')}
    </span>
  )
}

const KPI_TONE = {
  success: 'border-success/20 bg-success/[0.06] [&_[data-icon]]:bg-success/15 [&_[data-icon]]:text-success',
  danger: 'border-destructive/20 bg-destructive/[0.05] [&_[data-icon]]:bg-destructive/12 [&_[data-icon]]:text-destructive',
  info: 'border-info/20 bg-info/[0.05] [&_[data-icon]]:bg-info/15 [&_[data-icon]]:text-info',
  primary: 'border-primary/20 bg-primary/[0.05] [&_[data-icon]]:bg-primary/12 [&_[data-icon]]:text-primary',
  warning: 'border-warning/30 bg-warning/[0.08] [&_[data-icon]]:bg-warning/20 [&_[data-icon]]:text-warning-strong',
  neutral: 'bg-card [&_[data-icon]]:bg-muted [&_[data-icon]]:text-muted-foreground',
} as const

export function Kpi({ icon: Icon, label, value, footer, tone = 'neutral' }: { icon: LucideIcon; label: string; value: ReactNode; footer?: ReactNode; tone?: keyof typeof KPI_TONE }) {
  return (
    <div className={cn('flex min-w-0 items-start gap-3 rounded-xl border p-4 shadow-card', KPI_TONE[tone])}>
      <span data-icon className="flex size-10 shrink-0 items-center justify-center rounded-full">
        <Icon className="size-5" aria-hidden />
      </span>
      <div className="min-w-0 space-y-1">
        <p className="truncate text-sm font-medium text-muted-foreground">{label}</p>
        <p className="truncate text-xl font-semibold tracking-tight sm:text-2xl">{value}</p>
        {footer ? <div className="text-xs text-muted-foreground">{footer}</div> : null}
      </div>
    </div>
  )
}

export function Section({ title, icon: Icon, actions, children, className, bodyClassName }: { title: ReactNode; icon?: LucideIcon; actions?: ReactNode; children: ReactNode; className?: string; bodyClassName?: string }) {
  return (
    <section className={cn('min-w-0 rounded-xl border bg-card shadow-card', className)}>
      <header className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
        {Icon ? <Icon className="size-4 text-muted-foreground" aria-hidden /> : null}
        <h2 className="min-w-0 flex-1 font-semibold">{title}</h2>
        {actions}
      </header>
      <div className={cn('p-4', bodyClassName)}>{children}</div>
    </section>
  )
}
